import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { axios } from "./axiosFactory";
import { useUser } from "@/composables/user/user";
import { useAppStore } from "@/composables/appStore";
import { useAnonymousIdentifier } from "@/composables/anonymousIdentifier.client";

export enum RequestHeader {
  Token = "AuthorizationToken",
}

enum QueryParam {
  Locale = "webPrefixCode",
  CurrencyId = "primaryCurrencyId",
  AnonymousIdentifier = "anonymousIdentifier",
}

export enum TokenAdd {
  Never,
  IfAvailable,
  Always,
}

export enum HttpMethod {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Patch = "PATCH",
  Delete = "DELETE",
}

const DefaultTokenAdd = TokenAdd.IfAvailable;

export type ApiRequestConfig = {
  method: HttpMethod;
  url: string;
  /** Query string parameters. Merged on top of the implicit locale/currency params. */
  params?: object;
  /** Request body. Ignored by the server for GET requests. */
  data?: object;
  /**
   * Controls the auth token (`AuthorizationToken`) header:
   * - `TokenAdd.Never` — never attach the token
   * - `TokenAdd.IfAvailable` (default) — attach it only when a user is logged in
   * - `TokenAdd.Always` — attach it, and throw when no user is logged in
   */
  tokenAdd?: TokenAdd;
  /**
   * When `true`, attach the persistent anonymous identifier as the
   * `anonymousIdentifier` query parameter so the backend can attribute the
   * request to an anonymous visitor (browse / AI-identification funnels).
   * Sent for logged-in users too — it bridges their pre-registration history.
   * Analytics-only: the backend silently ignores an invalid/over-long value.
   */
  sendAnonymousIdentifier?: boolean;
  timeout?: number;
  signal?: AbortSignal;
};

export const ApiClient = {
  async request<ResponseDataType>(config: ApiRequestConfig): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>(config);
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Get, url, params, tokenAdd: TokenAdd.Always })`. */
  async get<ResponseDataType>(url: string, params: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Get, url, params, tokenAdd: TokenAdd.Always });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Get, ... })`. */
  async getPublic<ResponseDataType>(url: string, params: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable, timeout?: number, signal?: AbortSignal): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Get, url, params, tokenAdd: addAuthToken, timeout, signal });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Post, ..., tokenAdd: TokenAdd.Always })`. */
  async post<ResponseDataType>(url: string, params: object = {}, data: object = {}, timeout?: number): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Post, url, params, data, tokenAdd: TokenAdd.Always, timeout });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Post, ... })`. */
  async postPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable, timeout?: number, signal?: AbortSignal): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Post, url, params, data, tokenAdd: addAuthToken, timeout, signal });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Put, ..., tokenAdd: TokenAdd.Always })`. */
  async put<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Put, url, params, data, tokenAdd: TokenAdd.Always });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Put, ... })`. */
  async putPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Put, url, params, data, tokenAdd: addAuthToken });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Delete, ..., tokenAdd: TokenAdd.Always })`. */
  async delete<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Delete, url, params, data, tokenAdd: TokenAdd.Always });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Delete, ... })`. */
  async deletePublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Delete, url, params, data, tokenAdd: addAuthToken });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Patch, ..., tokenAdd: TokenAdd.Always })`. */
  async patch<ResponseDataType>(url: string, params: object = {}, data: object = {}): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Patch, url, params, data, tokenAdd: TokenAdd.Always });
  },

  /** @deprecated Use `ApiClient.request({ method: HttpMethod.Patch, ... })`. */
  async patchPublic<ResponseDataType>(url: string, params: object = {}, data: object = {}, addAuthToken: TokenAdd = TokenAdd.IfAvailable): Promise<AxiosResponse<ResponseDataType>> {
    return makeRequest<ResponseDataType>({ method: HttpMethod.Patch, url, params, data, tokenAdd: addAuthToken });
  },
};

async function makeRequest<ResponseDataType>(request: ApiRequestConfig): Promise<AxiosResponse<ResponseDataType>> {
  const appStore = useAppStore();
  const { user } = useUser();
  const tokenAdd = request.tokenAdd ?? DefaultTokenAdd;

  const params: Record<string, unknown> = {
    [QueryParam.Locale]: appStore.countryCode.value,
    [QueryParam.CurrencyId]: appStore.currencyId.value,
    ...request.params,
  };

  if (request.sendAnonymousIdentifier) {
    params[QueryParam.AnonymousIdentifier] = useAnonymousIdentifier().getIdentifier();
  }

  const config: AxiosRequestConfig = {
    url: request.url,
    method: request.method,
    params,
    data: request.data ?? {},
  };

  if (tokenAdd === TokenAdd.Always) {
    if (user.value === undefined) {
      throw new Error("Cannot get token when no user is logged in.");
    }
    addHeader(config, RequestHeader.Token, user.value.token);
  } else if (tokenAdd === TokenAdd.IfAvailable && user.value) {
    addHeader(config, RequestHeader.Token, user.value.token);
  }

  if (request.timeout !== undefined) {
    config.timeout = request.timeout;
  }

  if (request.signal !== undefined) {
    config.signal = request.signal;
  }

  return axios.request<ResponseDataType>(config);
}

function addHeader(config: AxiosRequestConfig, name: string, value: string): void {
  if (typeof config.headers === "undefined") {
    config.headers = {};
  }

  config.headers[name] = value;
}
